/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2022 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

import React, { useState, useCallback, useEffect, useRef } from "react";
import AssetSetupModal from "./assetModal";
import { TimeSeriesMetric } from "../../lib/Types";

interface SourceInfo {
  source: string;
  id: number;
  sub_source: string;
}

interface Asset {
  id: string;
  name: string;
  scaleos_name: string;
  scaleos_type: string;
  power_capacity: number;
  source: string;
  manufacturer: string;
  model: string;
  serial_number: string;
  mounting: string;
  trigger_alarms: boolean;
  weight: boolean;
  energy_capacity: number;
  genset_use: string;
  run_hours_limit: number;
  source_info: SourceInfo[];
  edgeId: string | null;
}

interface SiteAssetOption {
  uuid: string;
  scaleos_name?: string | null;
  source_name?: string | null;
  name?: string | null;
}

interface Asset {
  id: string;
  name: string;
  scaleosName: string;
  scaleosType: string;
  outputCapacity: number;
  source: string;
  manufacturer: string;
  model: string;
  serialNumber: string;
  solarMounting: string;
  enableAlarms: boolean;
  showInScale: boolean;
  storageCapacity: number;
  gensetUse: string;
  runHoursLimit: number;
  sourceInfo: SourceInfo[];
  edgeId: string | null;
  parentAsset?: string | null;
  timeSeries?: TimeSeriesMetric[];
}
interface Props {
  assetsList: Asset[];
  site: {
    uuid: string;
    name: string;
    edge_id: string;
  };
  siteAssets?: SiteAssetOption[];
  isOpen: boolean;
  onClose: () => void;
  onSaveAsset: (updatedAsset: Asset) => void;
}

export default function AssetPreFillModal({
  assetsList,
  site,
  siteAssets = [],
  isOpen,
  onClose,
}: Props) {
  const [setupAssetsList, setSetupAssetsList] = useState(assetsList);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [dirty, setDirty] = useState<boolean>(false);
  const [activeAsset, setActiveAsset] = useState(assetsList[0]);
  const [savedAsset, setSavedAsset] = useState<AssetEndpoint | null>(null);
  const isFirstRender = useRef(true);
  const handlePrev = useCallback(() => {
    setDirty(false);
    setSelectedIndex((prev) => {
      const newIndex = Math.max(prev - 1, 0);
      setActiveAsset(setupAssetsList[newIndex]);
      return newIndex;
    });
  }, [setupAssetsList]);

  const handleNext = useCallback(() => {
    setDirty(false);
    setSelectedIndex((prev) => {
      const newIndex = Math.min(prev + 1, setupAssetsList.length - 1);
      setActiveAsset(setupAssetsList[newIndex]);
      return newIndex;
    });
  }, [setupAssetsList]);

  useEffect(() => {
    setSetupAssetsList(assetsList);
    setActiveAsset(assetsList[0]);
    setSelectedIndex(0);
  }, [assetsList, isOpen]);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return; // Skip first render
    }

    const nextIndex = Math.min(selectedIndex + 1, setupAssetsList.length - 1);
    const nextAsset = setupAssetsList[nextIndex];
    if (selectedIndex >= setupAssetsList.length - 1) {
      onClose(); // Close when clicked on
    }
    if (!nextAsset) return;

    // Fields that are updated with savedAsset values if not present in nextAsset
    const fieldMappings = {
      scaleosType: "scaleos_type",
      outputCapacity: "power_capacity",
      manufacturer: "manufacturer",
      model: "model",
      solarMounting: "mounting",
      enableAlarms: "trigger_alarms",
      showInScale: "weight",
      storageCapacity: "energy_capacity",
      gensetUse: "genset_use",
      runHoursLimit: "run_hours_limit",
    };

    const updatedAssetPreFill = {
      ...nextAsset,
      ...Object.fromEntries(
        Object.entries(fieldMappings).map(([key, savedKey]) => [
          key,
          nextAsset[key] ?? savedAsset?.[savedKey],
        ]),
      ),
    };

    // Saved row: fall back only for undefined so deliberate null clears
    // (e.g. model reset on manufacturer change) are preserved.
    const pick = (primary: unknown, fallback: unknown) =>
      primary !== undefined ? primary : fallback;

    setActiveAsset(updatedAssetPreFill);

    setSetupAssetsList((prevAssetsList) => {
      const updatedAssetsList = [...prevAssetsList];
      const current = prevAssetsList[selectedIndex];
      updatedAssetsList[selectedIndex] = {
        ...current,
        ...Object.fromEntries(
          Object.entries(fieldMappings).map(([key, savedKey]) => [
            key,
            pick(savedAsset?.[savedKey], current[key]),
          ]),
        ),
        id: savedAsset?.uuid ?? current.id,
        name: savedAsset?.name ?? current.name,
        scaleosName: savedAsset?.scaleos_name ?? current.scaleosName,
        sourceInfo: savedAsset?.source_info ?? current.sourceInfo,
        serialNumber: savedAsset?.serial_number ?? current.serialNumber,
        parentAsset: savedAsset?.parent_asset ?? null,
        timeSeries: savedAsset?.time_series ?? current.timeSeries ?? [],
      };
      return updatedAssetsList;
    });
    setDirty(selectedIndex < setupAssetsList.length - 1);
    setSelectedIndex(nextIndex);
  }, [savedAsset]);

  return setupAssetsList?.length ? (
    <AssetSetupModal
      key={selectedIndex} // This is to re-render the modal and animate it
      asset={activeAsset}
      site={site}
      siteAssets={siteAssets}
      preFill={true}
      isOpen={isOpen}
      onClose={onClose}
      onPrev={selectedIndex > 0 ? handlePrev : undefined}
      onNext={selectedIndex < setupAssetsList.length - 1 ? handleNext : undefined}
      onSaveAsset={(updatedAsset) => {
        setSavedAsset((prev) => ({ ...prev, ...updatedAsset }));
      }}
      index={selectedIndex}
      preFillDirty={dirty}
    />
  ) : null;
}
